import { useClearOrgSalesforceLink } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import { SalesforceLinkFormConflict } from '@/features/SalesforceAccounts/components/LinkConflictPanel'
import {
  SalesforceCurrentAccountId,
  SalesforceReasonField,
} from '@/features/SalesforceAccounts/components/SalesforceLinkFields'
import { SalesforceLinkFormDialog } from '@/features/SalesforceAccounts/components/SalesforceLinkFormDialog'
import {
  expectedCurrentFields,
  useSalesforceLinkForm,
} from '@/features/SalesforceAccounts/hooks/useSalesforceLinkForm'
import { Alert, Button } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SalesforceLinkFormProps } from './salesforceLinkFormProps'

/**
 * Clear: destroy the link. There is no id field — nothing is being created,
 * only named — so the only input is the required `reason`.
 *
 * account-service is the sole store of this value, so a wrong clear destroys
 * the only copy of the answer. Hence the compare-and-swap token sent verbatim
 * from the card's GET, and an absent `previousSalesforceAccountId` on a 409
 * leaving the operator with a re-read and nothing else.
 */
export function ClearAccountForm({
  orgId,
  currentAccountId,
  onSuccess,
  onReRead,
}: Readonly<SalesforceLinkFormProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [reason, setReason] = useState('')
  const hasReason = reason.trim().length > 0

  const form = useSalesforceLinkForm({
    orgId,
    accountIds: currentAccountId ? [currentAccountId] : [],
    toastId: 'salesforce-clear-account',
    toastAccountId: currentAccountId,
    onSuccess,
    onReRead,
    useLinkMutation: useClearOrgSalesforceLink,
    buildVariables: (token) => ({
      pathParams: { orgId },
      body: {
        reason: reason.trim(),
        ...expectedCurrentFields(token),
      },
    }),
    canSubmit: hasReason,
  })

  return (
    <SalesforceLinkFormDialog
      title={String(t('salesforceLink.actions.clear.title'))}
      errorTitle={String(t('salesforceLink.actions.clear.error.title'))}
      errorDescription={form.errorDescription}
      conflictPanel={
        form.isConflict ? (
          <SalesforceLinkFormConflict
            form={form}
            expectedAccountId={currentAccountId}
            variant="clear"
          />
        ) : undefined
      }
      cancelLabel={String(t('salesforceLink.actions.clear.cancel'))}
      submitButton={
        <Button
          colorPalette="red"
          disabled={form.submitDisabled}
          loading={form.isBusy}
          onClick={() => form.submit(currentAccountId)}
        >
          {String(t('salesforceLink.actions.clear.submit'))}
        </Button>
      }
    >
      {/* Static, and shown the moment the dialog opens: no `role="alert"`, or
          every open would fire an assertive interruption. */}
      <Alert.Root status="warning">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Description>
            {String(t('salesforceLink.actions.clear.warning'))}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>

      <SalesforceCurrentAccountId
        label={String(t('salesforceLink.actions.clear.currentAccountIdLabel'))}
        value={currentAccountId}
      />

      <SalesforceReasonField
        label={String(t('salesforceLink.actions.clear.fields.reason.label'))}
        placeholder={String(
          t('salesforceLink.actions.clear.fields.reason.placeholder'),
        )}
        helper={String(t('salesforceLink.actions.clear.fields.reason.helper'))}
        value={reason}
        isRequired
        error={form.fieldError}
        onChange={(value) => {
          form.clearFieldErrors()
          setReason(value)
        }}
      />
    </SalesforceLinkFormDialog>
  )
}
